import logger from '@utils/logger'
import { reloadCurrentPage } from '@utils/browserLifecycle'
import {
  isSensitiveTelemetryRoute,
  sanitizeTelemetryPayload
} from '@/privacy/telemetrySanitizer'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'

let sentryModule = null
let initializationPromise = null
let shutdownPromise = null
let sentryAllowed = false
let initializationGeneration = 0
let restartRequiresReload = false

function hasTelemetryPermission() {
  const currentUrl =
    typeof window === 'undefined'
      ? ''
      : `${window.location.pathname}${window.location.search}${window.location.hash}`

  return (
    isPrivacyCategoryAllowed('analytics') &&
    !isSensitiveTelemetryRoute(currentUrl)
  )
}

function canSendTelemetry() {
  return sentryAllowed && hasTelemetryPermission()
}

function sanitizeAllowedTelemetry(payload) {
  return canSendTelemetry() ? sanitizeTelemetryPayload(payload) : null
}

function applyCachedUser(Sentry) {
  try {
    const cachedUser = JSON.parse(window.localStorage.getItem('user'))
    if (cachedUser?.id) Sentry.setUser({ id: cachedUser.id })
  } catch (_error) {
    // Missing or invalid cached account data should not affect monitoring.
  }
}

/**
 * Sentry Configuration for Client
 * Error tracking and monitoring for React application
 */

/**
 * Initialize Sentry for client-side error tracking
 */
export function initializeSentry() {
  // Check if Sentry is enabled via environment variable
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  const environment =
    import.meta.env.VITE_ENV || import.meta.env.MODE || 'development'
  const enabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false' && sentryDsn

  if (!enabled || !sentryDsn) {
    logger.info('Sentry not configured - client-side error tracking disabled')
    return null
  }

  if (!hasTelemetryPermission()) {
    sentryAllowed = false
    return null
  }

  sentryAllowed = true
  if (sentryModule) return Promise.resolve(sentryModule)
  if (initializationPromise) return initializationPromise
  if (shutdownPromise || restartRequiresReload) {
    const generation = initializationGeneration
    let restartPromise
    const shutdown = shutdownPromise || Promise.resolve()

    restartPromise = shutdown.then(() => {
      if (initializationPromise === restartPromise) {
        initializationPromise = null
      }
      if (!canSendTelemetry() || generation !== initializationGeneration) {
        return null
      }
      reloadCurrentPage()
      return null
    })
    initializationPromise = restartPromise
    return restartPromise
  }

  try {
    const generation = ++initializationGeneration
    // Dynamic import to avoid bundling Sentry in development if not needed
    let pendingInitialization
    pendingInitialization = import('@sentry/react')
      .then(Sentry => {
        if (!sentryAllowed || generation !== initializationGeneration)
          return null

        Sentry.init({
          dsn: sentryDsn,
          environment,
          integrations: [Sentry.browserTracingIntegration()],
          // Performance Monitoring
          tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
          // Filter out sensitive data
          beforeBreadcrumb: breadcrumb => sanitizeAllowedTelemetry(breadcrumb),
          beforeSend: event => sanitizeAllowedTelemetry(event),
          beforeSendTransaction: event => sanitizeAllowedTelemetry(event),
          // Ignore certain errors
          ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'atomicFindClose',
            // Network errors that are expected
            'NetworkError',
            'Network request failed',
            'Failed to fetch',
            // Chrome extensions
            'chrome-extension://',
            'moz-extension://'
          ],
          // Filter out certain URLs
          denyUrls: [
            // Browser extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i
          ]
        })

        sentryModule = Sentry
        applyCachedUser(Sentry)

        logger.info('Sentry initialized for client', {
          environment,
          dsn: sentryDsn.substring(0, 20) + '...'
        })

        return Sentry
      })
      .catch(error => {
        if (initializationPromise === pendingInitialization) {
          initializationPromise = null
        }
        logger.warn('Failed to initialize Sentry:', error.message)
        return null
      })
    initializationPromise = pendingInitialization
    return pendingInitialization
  } catch (error) {
    initializationPromise = null
    logger.warn('Sentry initialization error:', error.message)
    return null
  }
}

export function disableSentry() {
  sentryAllowed = false
  initializationGeneration += 1
  initializationPromise = null

  if (shutdownPromise) return shutdownPromise

  const moduleToClose = sentryModule
  sentryModule = null

  if (!moduleToClose) return Promise.resolve()
  restartRequiresReload = true

  let pendingShutdown
  pendingShutdown = (async () => {
    try {
      moduleToClose.setUser(null)
      await moduleToClose.close(1000)
    } catch (_error) {
      // Monitoring shutdown must not affect the product experience.
    } finally {
      if (shutdownPromise === pendingShutdown) shutdownPromise = null
    }
  })()
  shutdownPromise = pendingShutdown
  return pendingShutdown
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user) {
  if (!canSendTelemetry() || !sentryModule || !user?.id) return
  sentryModule.setUser({ id: user.id })
}

/**
 * Clear user context
 */
export function clearSentryUser() {
  if (!sentryModule) return
  sentryModule.setUser(null)
}

/**
 * Capture exception
 */
export function captureException(error, context = {}) {
  if (!canSendTelemetry() || !sentryModule) return
  sentryModule.captureException(error, {
    extra: context,
    tags: {
      component: context.component || 'unknown'
    }
  })
}

/**
 * Capture message
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!canSendTelemetry() || !sentryModule) return
  sentryModule.captureMessage(message, {
    level,
    extra: context,
    tags: {
      component: context.component || 'unknown'
    }
  })
}
